import { X } from "lucide-react";
import { useEffect, useState } from "react";

interface Props {
  open: boolean;
  currentVersionNumber: number | null;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
}

export function LockBlueprintModal({ open, currentVersionNumber, onClose, onConfirm }: Props) {
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) setBusy(false);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const handleConfirm = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await onConfirm();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="lock-blueprint-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
    >
      <button
        type="button"
        aria-label="Close lock blueprint dialog"
        className="absolute inset-0 h-full w-full cursor-default"
        onClick={onClose}
      />
      <div className="relative z-50 w-full max-w-lg rounded-lg bg-card p-5 shadow-xl">
        <div className="flex items-start justify-between">
          <div>
            <h3 id="lock-blueprint-title" className="text-lg font-semibold text-text-primary">
              Lock blueprint
              {currentVersionNumber != null && (
                <span className="ml-2 text-sm font-normal text-text-faint">
                  v{currentVersionNumber}
                </span>
              )}
            </h3>
            <p className="mt-2 text-sm text-text-muted">
              Locking creates an immutable version. Downstream artefacts will be generated against
              this version. You can unlock and relock later if requirements change.
            </p>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="rounded p-1 text-text-faint hover:bg-card-hover"
          >
            <X size={16} />
          </button>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded px-3 py-1.5 text-sm text-text-secondary hover:bg-card-hover"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={busy}
            className="rounded bg-accent px-3 py-1.5 text-sm font-medium text-white hover:bg-accent/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Lock blueprint
          </button>
        </div>
      </div>
    </div>
  );
}
